import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

/**
 * ProtectedRoute - Gates access based on badges, roles, and ward restrictions
 * 
 * Usage:
 * <ProtectedRoute requiredBadge="councillor">
 *   <CouncillorDashboard />
 * </ProtectedRoute>
 * 
 * <ProtectedRoute requiredPermission="admin.users">
 *   <AdminPanel />
 * </ProtectedRoute>
 * 
 * <ProtectedRoute requiredBadge="councillor" wardParam="ward">
 *   <WardMessages />  // Will check if councillor can access the ward from URL params
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ 
  children, 
  requiredBadge,      // Single badge required (e.g., 'councillor', 'mayor')
  requiredBadges,     // Array of badges (user needs at least one)
  requiredPermission, // Permission key from PERMISSIONS object
  requiredRole,       // Role required (e.g., 'admin', 'community_leader')
  wardParam,          // URL param name containing ward to check access for
  allowReadOnly = true, // Allow former officials with read-only access
  redirectTo = '/',   // Where to redirect if access denied
  showAccessDenied = false, // Show access denied message instead of redirect
}) => {
  const { user, loading } = useAuth();
  const permissions = usePermissions();
  const location = useLocation();

  // Still loading auth state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A52A]"></div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  let hasAccess = false;
  let accessDeniedReason = '';

  // Check role requirement
  if (requiredRole) {
    if (permissions.role === requiredRole || permissions.isAdmin) {
      hasAccess = true;
    } else {
      accessDeniedReason = `Requires ${requiredRole} role`;
    }
  }

  // Check single badge requirement
  if (requiredBadge && !hasAccess) {
    if (permissions.hasBadge(requiredBadge) || permissions.isAdmin) {
      hasAccess = true;
    } else {
      accessDeniedReason = `Requires ${requiredBadge} badge`;
    }
  }

  // Check multiple badges (needs at least one)
  if (requiredBadges && !hasAccess) {
    if (requiredBadges.some(badge => permissions.hasBadge(badge)) || permissions.isAdmin) {
      hasAccess = true;
    } else {
      accessDeniedReason = `Requires one of: ${requiredBadges.join(', ')}`;
    }
  }

  // Check permission key
  if (requiredPermission && !hasAccess) {
    if (permissions.can(requiredPermission)) {
      hasAccess = true;
    } else {
      accessDeniedReason = `Requires ${requiredPermission} permission`;
    }
  }

  // If no specific requirements, just require login
  if (!requiredRole && !requiredBadge && !requiredBadges && !requiredPermission) {
    hasAccess = true;
  }

  // Check ward access if wardParam specified
  if (hasAccess && wardParam) {
    const urlParams = new URLSearchParams(location.search);
    const ward = urlParams.get(wardParam) || location.pathname.split('/').find(p => p.startsWith('Ward'));
    
    if (ward && !permissions.canAccessWard(ward)) {
      hasAccess = false;
      accessDeniedReason = `You don't have access to ${ward}`;
    }
  }

  // Check read-only restriction
  if (hasAccess && !allowReadOnly && permissions.isReadOnly()) {
    hasAccess = false;
    accessDeniedReason = 'This action requires an active (non-former) role';
  }

  // Access denied
  if (!hasAccess) {
    if (showAccessDenied) {
      return (
        <div className="min-h-screen bg-black flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-red-800 rounded-lg p-8 max-w-md text-center">
            <div className="text-red-500 text-4xl mb-4">🚫</div>
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-gray-400 font-mono text-sm mb-4">{accessDeniedReason}</p>
            <a 
              href={redirectTo} 
              className="inline-block bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded font-mono text-sm"
            >
              Go Back
            </a>
          </div>
        </div>
      );
    }
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

/**
 * Higher-order component version for class components or convenience
 */
export const withProtection = (WrappedComponent, options = {}) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute {...options}>
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
};

export default ProtectedRoute;
